/**
* 共通ヘルパー
*/
import axios from '@/plugins/axios';
import cf from '@/mixins/commonFunctions.js';

const actions = {
  /** ロケーションからenvを判別 */
  checkEnv({ commit, state }) {
    if (state.env.flag) return;

    const data = {
      flag: true,
      name: process.env.NODE_ENV,
    };

    commit('putEnv', data);
  },

  checkSystemMaster({ commit, rootState }) {
    const systemMasters = [
      'imauji@cicac.jp',
      'sonoda@cicac.jp',
      'fujikawa@cicac.jp',
      'yamaguchi@cicac.jp',
      'amagawa@cicac.jp',
      'iguchi@cicac.jp',
    ];
    const flag = systemMasters.includes(rootState.user.email);
    commit('isSystemMaster', flag);
  },

  reset({ commit }) {
    commit('reset');
  },

  checkDisplayDirection({ commit }) {
    const direction = window.innerHeight < window.innerWidth ? 'horizontal' : 'vertical';
    commit('setDisplayDirection', direction);
  },

  /** backend共通マスターデータ */
  getMaster({ commit }) {
    axios({
      method: 'GET',
      url: '/v1/connection/master',
    })
      .then((response) => {
        commit('putMaster', response.data.master);
      })
      .catch((error) => {
        if (error.response) console.log(error.response.data);
        else console.log(error);
      });
  },

  /**
   * ステータス色を取得してストアに保存
   * @param {number|number[]} officeIds - 事業所ID、または事業所IDの配列
   */
  async fetchStatusColors({ commit }, officeIds) {
    const ids = (Array.isArray(officeIds) ? officeIds : [officeIds]).map(Number).filter(Boolean);
    if (!ids.length) return;
    try {
      const response = await axios({
        method: 'GET',
        url: '/v1/statusColor/get/list',
        params: { officeIds: ids },
      });
      const map = response.data?.statusColors || {};
      Object.keys(map).forEach((id) => {
        commit('setStatusColors', { officeId: Number(id), colors: map[id] });
      });
    } catch (error) {
      console.error('Failed to fetch status colors:', error);
    }
  },

  getMessageTemplate({ commit, rootState }) {
    axios({
      method: 'GET',
      url: '/v1/messageTemplate/get/list',
      params: {
        officeId: rootState.user.offices?.[0]?.id,
        withAdminTemplate: 1,
      },
    })
      .then((response) => {
        commit('putMessageTemplate', response.data.messageTemplates.data);
      })
      .catch((error) => {
        if (error.response) console.log(error.response.data);
        else console.log(error);
      });
  },

  getDemandSchedules({ commit }, demandAreaIds) {
    if (demandAreaIds?.length) {
      const now = new Date();
      const params = {
        demandAreaIds,
        targetYear: Number(cf.methods.formatTimestamp(now, 'YYYY')),
        targetMonth: Number(cf.methods.formatTimestamp(now, 'MM')),
        targetDate: Number(cf.methods.formatTimestamp(now, 'DD')),
      };
      
      axios({
        method: 'GET',
        url: '/v1/demandSchedule/get/list',
        params,
      })
        .then((response) => {
          commit('putDemandSchedules', response.data.demandSchedules.data);
        })
        .catch((error) => {
          if (error.response) console.log(error.response.data);
          else console.log(error);
        });
    } else {
      commit('putDemandSchedules', []);
    }
  },

  checkIsNativeApp({ dispatch, commit }) {
    const UA = navigator.userAgent;
    const data = {
      app: UA.includes('MITT_APP'),
      android: UA.includes('ANDROID'),
      iOS: UA.includes('iOS'),
    };
    commit('isNativeApp', data);
    if (data.app && (data.android || data.iOS)) dispatch('checkDriverAppVersion', data);
  },

  checkDriverAppVersion({ commit }, data) {
    axios({
      method: 'GET',
      url: '/v1/driverAppVersion/get/list',
      params: {
        limit: 1,
        type: data.android ? 1 : 2,
      },
    })
      .then((response) => {
        const res = response.data;
        const versionStr = res.driverAppVersions?.data[0]?.version || null;
        commit('setDriverAppVersion', versionStr);
      })
      .catch((error) => {
        if (error.response) console.log(error.response.data);
        else console.log(error);
      });
  },
  async checkDevice({ commit }, uuid) {
    let deviceInfo = {
      uuid,
      officeId: null,
      officeName: null,
      demandAreaIds: [],
      isChecked: false,
      // 通信が失敗した場合に立てるフラグ
      // 失敗時も officeId は null のままなので、これがないと「端末未登録」と区別できない
      checkFailed: false,
    };

    if (uuid) {
      await axios({
        method: 'GET',
        url: '/v1/device/get/getOfficeIdByUuid',
        params: { uuid },
      })
        .then((response) => {
          const res = response.data;
          deviceInfo = {
            uuid,
            officeId: res.officeId || null,
            officeName: res.officeName || null,
            deviceName: res.deviceName || null,
            demandAreaIds: res.demandAreaIds || [],
            isChecked: true,
            // 取得に成功したので、前回の失敗フラグは必ず解除しておく
            checkFailed: false,
          };
        })
        .catch((error) => {
          if (error.response) console.log(error.response.data);
          else console.log(error);
          // 通信の失敗を「端末未登録」と取り違えないよう、失敗した事実を残す
          deviceInfo.checkFailed = true;
        });
    }

    deviceInfo.isChecked = true;
    commit('setDeviceInfo', deviceInfo);
  },

  countUpUpdateCount({ commit, state }, data) {
    console.log('countUpUpdateCount', data);
    if (Array.isArray(data.targets)) {
      data.targets.forEach((target) => {
        if (Object.keys(state.updateCount).includes(target)) commit('updateRefreshCount', target);
      });
    }
  },
};


const mutations = {
  putEnv(state, data) {
    state.env.flag = data.flag;
    state.env.name = data.name;
  },

  isSystemMaster(state, bool) {
    state.isSystemMaster = bool;
  },

  systemMasters(state, array) {
    state.systemMasters = array;
  },

  reset(state) {
    state.systemMasters = [];
    state.isSystemMaster = false;
    state.env = {
      name: 'production',
      flag: false,
    };
  },

  putMaster(state, data) {
    state.master = data;
  },

  putMessageTemplate(state, data) {
    state.messageTemplates = data;
  },

  putDemandSchedules(state, data) {
    state.demandSchedules = data;
  },

  isNativeApp(state, data) {
    state.isNative = data;
  },
  setDriverAppVersion(state, data) {
    state.nativeVersion = data;
  },

  setDisplayDirection(state, direction) {
    state.displayDirection = direction;
  },

  setDeviceInfo(state, data) {
    state.device = data;
  },

  updateRefreshCount(state, target) {
    state.updateCount[target] += 1;
  },

  setStatusColors(state, { officeId, colors }) {
    state.statusColorsByOffice = {
      ...state.statusColorsByOffice,
      [officeId]: colors,
    };
  },
};


const getters = {
  statusColorHex: (state) => (officeId, statusName) => {
    const override = state.statusColorsByOffice[officeId]?.[statusName];
    if (override) return override;
    const flags = state.master?.orders?.flags || [];
    const activities = state.master?.driverActivities?.items || [];
    const found = [...flags, ...activities].find((f) => f.name === statusName);
    return state.master?.colors?.[found?.color] || '#ADB5BD';
  },

  statusColorHexByFlagValue: (state, moduleGetters) => (officeId, flagValue) => {
    const flags = state.master?.orders?.flags || [];
    const activities = state.master?.driverActivities?.items || [];
    const found = [...flags, ...activities].find((f) => f.value === flagValue);
    return moduleGetters.statusColorHex(officeId, found?.name || '');
  },
};

const state = {
  isSystemMaster: false,
  isNative: {
    app: false,
    android: false,
    iOS: false,
  },
  nativeVersion: null,
  env: {
    name: 'production',
    flag: false,
  },
  master: {},
  messageTemplates: [],
  displayDirection: 'vertical',
  device: {
    uuid: null,
    officeId: null,
    officeName: null,
    deviceName: null,
    demandAreaIds: [],
    isChecked: false,
    checkFailed: false,
  },
  demandSchedules: [],
  updateCount: {
    threads: 0,
    talkrooms: 0,
  },
  statusColorsByOffice: {},
};

export default {
  namespaced: true,
  actions,
  mutations,
  getters,
  state,
};
